import { Autocomplete, Box } from "@mui/material";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import LoadingSpinner from "components/LoadingSpinner";
import { MecordColors } from "Constants/Color";
import { autocompleteDropdownStyles } from "Constants/Style";
import { AppDispatch } from "index";
import React, { useEffect, useState } from "react";
import { Button, Col, Form, ListGroup, Row } from "react-bootstrap";
import { BsPlus, BsTrash } from "react-icons/bs";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  addModuels,
  fetchModuleByID,
  updateModules as onUpdateModule,
} from "slices/UserManagement/module/thunk";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";

import { useFormik } from "formik";
import * as Yup from "yup";
import { clearTableState } from "helpers/ClearTableState/ClearTableState";
import { TableKeys } from "Constants/TableKeys/TableKeys";

const moduleFormSchema = Yup.object({
  appName: Yup.string().trim().required("Please enter App Name"),

  modules: Yup.array().min(1, "Please add at least one module"),

  newModuleName: Yup.string()
    .trim()
    .when("modules", {
      is: (modules: any) => !modules || modules.length === 0,
      then: (schema) => schema.required("Please enter Module Name"),
      otherwise: (schema) => schema.notRequired(),
    }),

  newModuleActions: Yup.array()
    .of(Yup.string())
    .when("modules", {
      is: (modules: any) => !modules || modules.length === 0,
      then: (schema) => schema.min(1, "Please select at least one permission"),
      otherwise: (schema) => schema.notRequired(),
    }),
});

const permissionOptions = ["View", "Create", "Update"];

const AddModuleForm = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState<Boolean>(false);

  //   Formik
  const formik = useFormik({
    initialValues: {
      appName: "",
      newModuleName: "",
      newModuleActions: [] as string[],
      modules: [] as { module_name: string; actions: string[] }[],
    },
    validationSchema: moduleFormSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      const module_name_perm = values.modules.reduce(
        (acc, mod) => {
          acc[mod.module_name] = mod.actions;
          return acc;
        },
        {} as Record<string, string[]>,
      );

      const payload = {
        app_name: values.appName.trim(),
        module_name_perm,
      };

      try {
        if (isEdit && id) {
          await dispatch(
            onUpdateModule({
              id: id,
              data: payload,
            }),
          ).unwrap();
          toast.success("Module updated successfully");
        } else {
          await dispatch(addModuels(payload)).unwrap();
          toast.success("Module added successfully");
          clearTableState(TableKeys?.USER_MANAGEMENT?.MODULE);
        }
        navigate("/master/module-list");
      } catch (error: any) {
        toast.error(
          error?.error ||
            error?.message ||
            "Something went wrong. Please try again.",
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  useScrollToFormikError(
    formik.errors,
    formik.submitCount,
    formik.isSubmitting,
  );

  const handleAddModule = () => {
    if (
      !formik.values.newModuleName.trim() ||
      formik.values.newModuleActions.length === 0
    ) {
      // formik.setTouched({
      //   newModuleName: true,
      //   newModuleActions: true,
      // });
      return;
    }

    formik.setFieldValue("modules", [
      ...formik.values.modules,
      {
        module_name: formik.values.newModuleName.trim(),
        actions: formik.values.newModuleActions,
      },
    ]);

    formik.setFieldValue("newModuleName", "");
    formik.setFieldValue("newModuleActions", []);
  };

  const handleRemoveModule = (index: number) => {
    formik.setFieldValue(
      "modules",
      formik.values.modules.filter((_, i) => i !== index),
    );
  };

  useEffect(() => {}, [formik.values]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    const fetchModule = async () => {
      try {
        const res = await dispatch(fetchModuleByID(id)).unwrap();

        const initialModules = Object.entries(
          res.data?.module_name_perm || {},
        ).map(([module_name, actions]) => ({
          module_name,
          actions: actions as string[],
        }));
        formik.setValues({
          appName: res?.data.app_name || "",
          newModuleName: "",
          newModuleActions: [],
          modules: initialModules,
        });
        formik.setTouched({});
        formik.setErrors({});
      } catch (error) {
        toast.error("Failed to load module data");
      } finally {
        setLoading(false);
      }
    };

    fetchModule();
  }, [id, isEdit]);

  return (
    <PageContainer>
      <PageBreadcrumb
        title={isEdit ? "Edit Module" : "Add Module"}
        titleLink="/master/module-list"
        breadcrumbs={[
          { label: "Module", to: "/master/module-list" },
          { label: isEdit ? "Edit Module" : "Add Module", to: "" },
        ]}
        backNavigationPath={-1}
      />
      <hr />
      {isEdit && loading ? (
        <LoadingSpinner />
      ) : (
        <Form onSubmit={formik.handleSubmit}>
          <Row className="mb-4">
            <Col md={6}>
              <Form.Group controlId="appName">
                <Form.Label className="fw-semibold">
                  App Name
                  <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  name="appName"
                  placeholder="Enter App Name"
                  value={formik.values.appName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.touched.appName && !!formik.errors.appName
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.appName}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-4 align-items-end">
            <Col xs={6}>
              <Form.Group controlId="moduleName">
                <Form.Label className="fw-semibold">
                  Module Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  id="newModuleName"
                  name="newModuleName"
                  placeholder="Enter Module Name"
                  value={formik.values.newModuleName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.touched.newModuleName &&
                    !!formik.errors.newModuleName
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.newModuleName}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col xs={6}>
              <Form.Group controlId="permissions">
                <Form.Label className="fw-semibold">
                  Permissions <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={permissionOptions}
                  value={formik.values.newModuleActions}
                  onChange={(_, newValue) =>
                    formik.setFieldValue("newModuleActions", newValue)
                  }
                  onBlur={() =>
                    formik.setFieldTouched("newModuleActions", true)
                  }
                  renderOption={(props, option, { selected }) => (
                    <li {...props} style={{ padding: 0 }}>
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.8,
                          width: "100%",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          cursor: "pointer",

                          // ✅ Hover state
                          "&:hover": {
                            backgroundColor: MecordColors.Hover,
                            color: MecordColors.ActiveText,
                          },

                          // ✅ Selected state
                          ...(selected && {
                            backgroundColor: MecordColors.ActiveBackground,
                            color: MecordColors.ActiveText,
                          }),
                        }}
                      >
                        {/* Permission label */}
                        <span>{option}</span>

                        {/* Remove icon */}
                        {selected && (
                          <span
                            style={{
                              cursor: "pointer",
                              marginLeft: "10px",
                              color: "#d32f2f",
                              fontWeight: "bold",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              formik.setFieldValue(
                                "newModuleActions",
                                formik.values.newModuleActions.filter(
                                  (p) => p !== option,
                                ),
                              );
                            }}
                          >
                            ✕
                          </span>
                        )}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      size="small"
                      id="newModuleActions"
                      name="newModuleActions"
                      placeholder={
                        formik.values.newModuleActions.length === 0
                          ? "Select permissions"
                          : ""
                      }
                      error={
                        !!formik.touched.newModuleActions &&
                        !!formik.errors.newModuleActions
                      }
                      helperText={
                        formik.touched.newModuleActions
                          ? formik.errors.newModuleActions
                          : ""
                      }
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                      }}
                    />
                  )}
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderTags={(value, getTagProps) =>
                    value.map((option: any, index: number) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Box
                          key={option}
                          component="span"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            backgroundColor: MecordColors.ActiveBackground,
                            borderRadius: "3px",
                            padding: "2px 6px",
                            margin: "1px",
                            fontSize: "0.75rem",
                          }}
                          {...tagProps}
                        >
                          {option}
                        </Box>
                      );
                    })
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&:hover fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ced4da",
                      },
                    },
                  }}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={6}>
              <Button
                variant="light"
                onClick={handleAddModule}
                className="d-flex align-items-center gap-2 px-4 py-2 fw-semibold"
                type="button"
                disabled={
                  !formik.values.newModuleName.trim() ||
                  formik.values.modules.some(
                    (mod) =>
                      mod.module_name.trim().toLowerCase() ===
                      formik.values.newModuleName.trim().toLowerCase(),
                  )
                }
              >
                <BsPlus size={20} />
                Add
              </Button>
            </Col>
          </Row>

          {formik.values.modules.length > 0 && (
            <>
              <h6>Added Modules</h6>
              <ListGroup className="mb-4">
                {formik.values.modules.map((mod, index) => (
                  <ListGroup.Item
                    key={index}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <strong>{mod.module_name}</strong> - Permissions:{" "}
                      {mod.actions.join(", ")}
                    </div>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleRemoveModule(index)}
                    >
                      <BsTrash />
                    </Button>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </>
          )}
          {formik.touched.modules && formik.errors.modules && (
            <div className="text-danger mb-3">
              {formik.errors.modules as string}
            </div>
          )}

          <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
            <CancelButton
              onClick={() => navigate(-1)}
              //   disabled={isSubmitting}
            />
            <PrimarySubmitButton
              label={isEdit ? "Update" : "Submit"}
              isLoading={formik.isSubmitting}
              loadingLabel={isEdit ? "Updating..." : "Submit..."}
            />
          </div>
        </Form>
      )}
    </PageContainer>
  );
};

export default AddModuleForm;
